import { OtnEvent } from '@models/ot_nationals/otn_event';
import { OtnGame } from '@models/ot_nationals/otn_game';
import { OtnGameOtnTeam } from '@models/ot_nationals/otn_game_otn_team';
import { OtnGameOtnTeamOtnPlayer } from '@models/ot_nationals/otn_game_otn_team_otn_player';
import { OtnGameOtnTeamPeriod } from '@models/ot_nationals/otn_game_otn_team_period';
import { OtnDriveOutline, OtnGameSituation, OtnPublicPlay } from '@models/ot_nationals/otn_live_stats';
import { Options, ReadyState } from 'react-use-websocket';

export const OtnWebSocketOptions: Options = {
	share: true,
	shouldReconnect: () => true,
	reconnectInterval: (attemptNumber) => Math.min(1000 * 2 ** attemptNumber, 30000), // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s max
	reconnectAttempts: 30,
	heartbeat: { message: 'ping', returnMessage: 'pong', timeout: 30000, interval: 5000 },
};

export const getConnectionStatus = (readyState: ReadyState) => {
	return {
		[ReadyState.CONNECTING]: 'Connecting',
		[ReadyState.OPEN]: 'Open',
		[ReadyState.CLOSING]: 'Closing',
		[ReadyState.CLOSED]: 'Closed',
		[ReadyState.UNINSTANTIATED]: 'Uninstantiated',
	}[readyState];
};

export enum OtnChannels {
	GAMES = 'otn_games',
	TEAMS = 'otn_games_otn_teams',
	PLAYERS = 'otn_games_otn_teams_otn_players',
	TEAMS_PERIODS = 'otn_games_otn_teams_periods',
	// Live stats (TECHENG-725 contract §7)
	SITUATION = 'otn_games_situation',
	PLAYS = 'otn_stats_plays',
}

export type OtnSocketMessageData = {
	otn_game?: OtnGame;
	otn_games_otn_team?: OtnGameOtnTeam;
	otn_games_otn_teams_otn_player?: OtnGameOtnTeamOtnPlayer;
	otn_games_otn_teams_period?: OtnGameOtnTeamPeriod;
	otn_game_event?: OtnEvent;
	// Live stats channels
	situation?: OtnGameSituation;
	otn_game_id?: string;
	plays?: OtnPublicPlay[];
	deleted_play_ids?: string[];
	/** The whole game's drive breakdown after the batch; absent from older API payloads. */
	drives?: OtnDriveOutline[];
};

export type OtnSocketMessage = {
	data: string;
	channel: string;
	sent_at: string;
};

// The API publishes live-stats rows bare (TECHENG-1028), while older publishers wrapped them as
// { otn_game }, { otn_games_otn_team } and so on. Each reader accepts either shape and returns
// the row only when it belongs to this game, so the hooks never act on a mismatched message.
const unwrap = <T>(data: OtnSocketMessageData, key: keyof OtnSocketMessageData): Partial<T> | undefined =>
	(data?.[key] ?? data) as Partial<T> | undefined;

export const readGameRow = (data: OtnSocketMessageData, gameId: string): OtnGame | null => {
	const row = unwrap<OtnGame>(data, 'otn_game');
	return row?.id === gameId ? (row as OtnGame) : null;
};

export const readTeamRow = (data: OtnSocketMessageData, gameId: string): OtnGameOtnTeam | null => {
	const row = unwrap<OtnGameOtnTeam>(data, 'otn_games_otn_team');
	return row?.otn_game_id === gameId && row.otn_team_id ? (row as OtnGameOtnTeam) : null;
};

export const readPlayerRow = (data: OtnSocketMessageData, gameId: string): OtnGameOtnTeamOtnPlayer | null => {
	const row = unwrap<OtnGameOtnTeamOtnPlayer>(data, 'otn_games_otn_teams_otn_player');
	return row?.otn_game_id === gameId && row.otn_player_id ? (row as OtnGameOtnTeamOtnPlayer) : null;
};

export const readPeriodRow = (data: OtnSocketMessageData): OtnGameOtnTeamPeriod | null => {
	const row = unwrap<OtnGameOtnTeamPeriod>(data, 'otn_games_otn_teams_period');
	return row?.otn_game_otn_team_id && row.period_id !== undefined ? (row as OtnGameOtnTeamPeriod) : null;
};

/**
 * Applies a live row on top of the row already on screen. Socket rows carry no relations, so a
 * relation the incoming row leaves out (or sends as null) keeps its current value instead of
 * blanking the team name, logo or periods.
 */
export const mergeLiveRow = <T extends object>(current: T, incoming: Partial<T>): T => {
	const merged = { ...current, ...incoming } as Record<string, unknown>;
	for (const [key, value] of Object.entries(current)) {
		if (value !== null && typeof value === 'object' && merged[key] == null) merged[key] = value;
	}
	return merged as T;
};
